import { Schemas } from '@api-client/generated/types';

export type Membership = Schemas.AdminMembershipRecordDto;
export type AdminRole = NonNullable<Schemas.User['adminRole']>;

export type AccessMapView = 'employee' | 'company';

export type AccessMapFilters = {
  term: string;
  role?: AdminRole;
};

export type EmployeeGroup = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  adminRole: Membership['adminRole'];
  memberships: Membership[];
};

export type CompanyGroup = {
  companyId: string;
  companyName: string;
  memberships: Membership[];
};

const matchesTerm = (membership: Membership, term: string): boolean => {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    membership.firstName,
    membership.lastName,
    membership.email,
    membership.companyName,
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
};

/** Apply the shared filters to the flat membership set before aggregation, so
 * every cut and the CSV export see the same rows. */
export const filterMemberships = (
  memberships: Membership[],
  filters: AccessMapFilters
): Membership[] =>
  memberships.filter(
    (membership) =>
      (!filters.role || membership.adminRole === filters.role) &&
      matchesTerm(membership, filters.term)
  );

/** Group filtered memberships by employee (which clients each CSM can access). */
export const groupByEmployee = (memberships: Membership[]): EmployeeGroup[] => {
  const groups = new Map<string, EmployeeGroup>();
  for (const membership of memberships) {
    const existing = groups.get(membership.userId);
    if (existing) {
      existing.memberships.push(membership);
    } else {
      groups.set(membership.userId, {
        userId: membership.userId,
        firstName: membership.firstName,
        lastName: membership.lastName,
        email: membership.email,
        adminRole: membership.adminRole,
        memberships: [membership],
      });
    }
  }
  return [...groups.values()].sort((a, b) =>
    `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
  );
};

/** Group filtered memberships by company (which CSMs access each client). */
export const groupByCompany = (memberships: Membership[]): CompanyGroup[] => {
  const groups = new Map<string, CompanyGroup>();
  for (const membership of memberships) {
    const existing = groups.get(membership.companyId);
    if (existing) {
      existing.memberships.push(membership);
    } else {
      groups.set(membership.companyId, {
        companyId: membership.companyId,
        companyName: membership.companyName,
        memberships: [membership],
      });
    }
  }
  return [...groups.values()].sort((a, b) =>
    a.companyName.localeCompare(b.companyName)
  );
};
